import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAppDispatch } from "store";
import { setGlobalComponent } from "store/global-components";
import { useAppTranslation } from "locale/useAppTranslation";
import {
  useFuelTypeServiceV1GetFuelTypeQuery,
  useFuelTypeServiceV1UpdateFuelTypeMutation,
} from "_services/modules/fuel_type";
import { toast } from "react-toastify";
import { errorHandler } from "_services/errorHandler";

interface EditFuelTypeFormValues {
  fuelTypeName: string;
  priceFuel: string;
  effectiveDate: string;
  description: string;
}

const schema = yup.object({
  fuelTypeName: yup.string().required("Fuel Type Name is required").max(100, "Max 100 characters"),
  priceFuel: yup.string().required("Price Fuel is required").test(
    "is-positive-number",
    "Price must be a positive number",
    (value) => {
      if (!value) return false;
      const num = Number(value.replace(/,/g, ""));
      return !isNaN(num) && num > 0;
    }
  ),
  effectiveDate: yup.string().required("Effective Date is required"),
  description: yup.string().max(500, "Max 500 characters").default(""),
}).required();

const useEditFuelTypeForm = (id: string | undefined) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useAppTranslation("fuelType");

  const { data: detail, isLoading } = useFuelTypeServiceV1GetFuelTypeQuery(
    { id: id! },
    { skip: !id }
  );

  const [updateFuelType] = useFuelTypeServiceV1UpdateFuelTypeMutation();

  const {
    handleSubmit,
    formState: { errors },
    register,
    setError,
    reset,
    control,
  } = useForm<EditFuelTypeFormValues>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (detail?.fuel_type_name && detail?.price_fuel !== undefined) {
      reset({
        fuelTypeName: detail.fuel_type_name,
        priceFuel: String(detail.price_fuel),
        effectiveDate: detail.effective_date ?? "",
        description: detail.description ?? "",
      });
    }
  }, [detail, reset]);

  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [pendingData, setPendingData] = useState<EditFuelTypeFormValues | null>(null);

  useEffect(() => {
    dispatch(
      setGlobalComponent({
        title: t("page-title-edit"),
        hasBackButton: true,
      })
    );
  }, [dispatch, t]);

  const onSubmit = (data: EditFuelTypeFormValues) => {
    setPendingData(data);
    setShowSaveModal(true);
  };

  const handleConfirmSave = async () => {
    if (!pendingData || !id) return;
    try {
      await updateFuelType({
        id,
        fuelTypeServiceV1UpdateFuelTypeBody: {
          fuel_type_name: pendingData.fuelTypeName,
          price_fuel: Number(pendingData.priceFuel.replace(/,/g, "")),
          effective_date: pendingData.effectiveDate,
          description: pendingData.description,
        },
      }).unwrap();
      toast.success(t("toast-update-success"));
      setShowSaveModal(false);
      navigate(-1);
    } catch (error: any) {
      if (error?.status === 409) {
        setError("fuelTypeName", {
          type: "validate",
          message: t("error-duplicate-exists"),
        });
        setShowSaveModal(false);
      } else if (error?.status === 404) {
        setShowSaveModal(false);
        navigate(-1);
      } else {
        errorHandler(error);
        setShowSaveModal(false);
      }
    }
  };

  const handleConfirmCancel = () => {
    setShowCancelModal(false);
    navigate(-1);
  };

  return {
    handleSubmit,
    errors,
    register,
    control,
    setError,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    isLoading,
    t,
  };
};

export default useEditFuelTypeForm;
